import React from 'react';
import { Waypoint } from 'react-waypoint';
import { ComplexValue, ElementLoader } from 'ui';
import { OverlayComponent } from 'app/components';
import { deepEquals } from 'app/infrastructure/js';
import { SearchFilters } from 'app/area/search-filters/search-filters';
import { SerializePayload } from 'app/area/search-filters/types';
import { TextConfigurationTranslation } from '../../../constants';
import { ListHeader } from '../../common/list-header';
import { predefinedFilters, textDefaultFilters, textFilters, textQueryFilters } from '../../text-filter-configuration';
import { excelExportFilename } from '../constants';
import { serialize } from '../serialize';
import { TextListResults } from './text-list-results';
import { TextListToolbar } from './text-list-toolbar';
import { ModifiedText, TextConfigurationListState, TextSearchModel } from '../types-store';
import { ExceededLengthError } from 'app/area/text-configuration/list/common/exceeded-length-error';

interface TextListRendererProps {
  listState: TextConfigurationListState;

  loadTextsList: (model: TextSearchModel, startIndex: number) => void;
  modifyText: (modifiedText: ModifiedText) => void;
  cancelChanges: () => void;
  saveChanges: (modifiedTexts: ModifiedText[],
    successMessage: string,
    validationErrorMessage: string) => void;

  exportToExcel: (
    model: TextSearchModel,
    maxResults: number,
    filename: string,
    startMessage: string,
    successMessage: string
  ) => void;
  openExcelImport: () => void;

  translation: TextConfigurationTranslation;

  language: ComplexValue<number>;
}

export const TextListRenderer: React.FC<TextListRendererProps> = ({
  listState, loadTextsList, modifyText, cancelChanges, saveChanges,
  translation, exportToExcel, openExcelImport, language
}) => {

  const { listItems, modifiedItems, numberOfResults, exportExcelFetching, importExcelFetching,
    requestedSearchModel, fetching, updating, exceededLengthError, validation } = listState;

  const updateSearchModel = (payload: SerializePayload) => {
    onLoadData(serialize(payload));
  };

  const onLoadData = (searchModel: TextSearchModel, startIndex?: number) => {

    if (startIndex !== undefined || !deepEquals(searchModel, requestedSearchModel)) {
      loadTextsList(searchModel, startIndex);
    }
  };

  const onFetchMore = () => {

    if (fetching || !listItems || listItems.length === 0 || listItems.length === numberOfResults) {
      return;
    }

    onLoadData(requestedSearchModel, listItems.length);
  };

  const onSaveChanges = () => {
    saveChanges(modifiedItems,
      translation.message_texts_updated,
      translation.text_validation_error);
  };

  const onExportToExcel = () => {
    listItems && listItems.length > 0 && exportToExcel(
      requestedSearchModel,
      numberOfResults,
      excelExportFilename,
      translation.excel_started,
      translation.excel_success
    );
  };

  const rightToolbar = <TextListToolbar
    onExportToExcel={onExportToExcel}
    openExcelImport={openExcelImport}
    exportExcelFetching={exportExcelFetching}
    importExcelFetching={importExcelFetching}
    totalResultHits={numberOfResults}
    translation={translation}
  />;

  const changedItems = (modifiedItems || []).filter(text => !text.onlyWhitespace);
  const allValid = (validation?.text?.messages?.length ?? 0) === 0 &&
    !modifiedItems.some(text => text.text.trim() === '');

  return <>
    <OverlayComponent
      displayOverlay={updating}
      overlayText={translation.saving_texts} />
    <SearchFilters
      displayInfoText={true}
      topToolbar={<div className="row">
        <h1 className="col-12">{translation.text_configuration_title}</h1>
      </div>}
      rightToolbar={rightToolbar}
      filtersConfig={{ area: 'textConfiguration' }}
      queryFilters={textQueryFilters(translation)}
      predefinedFilters={predefinedFilters(translation)}
      customFilters={textFilters(translation)}
      defaultFilterValues={textDefaultFilters(language, translation)}
      updateSearchModel={updateSearchModel}>
      <ExceededLengthError
        errors={exceededLengthError}
        hint={translation.text_exceeded_length_hint} />
      {!!listItems && <ListHeader
        numberOfChanges={changedItems.length}
        numberOfResults={numberOfResults}
        allValid={allValid}
        cancelChanges={cancelChanges}
        saveChanges={onSaveChanges}
        updateLabel={translation.update_texts}
        modifiedLabel={translation.modified_texts}
        noHitsLabel={translation.search_no_hits}
        translation={translation} />}
      {listItems && listItems.length > 0
        ? <TextListResults
          textsList={listItems}
          modifiedTexts={modifiedItems}
          modifyText={modifyText}
          validation={validation}
          translation={translation} />
        : null}
      <Waypoint key="wpTextConfiguration" onEnter={onFetchMore} scrollableAncestor={window} bottomOffset="-10%" />
      {fetching ? <ElementLoader /> : null}
    </SearchFilters>
  </>;
};
